import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronRight, FiChevronDown, FiVideo, FiRotateCw, FiSun, FiMusic, FiStar, FiArrowLeft, FiArrowRight, FiExternalLink, FiSmartphone, FiMail, FiMessageSquare, FiWifi } from 'react-icons/fi';
import { BsQrCode, BsWhatsapp } from 'react-icons/bs';
import VideoBubble from './VideoBubble';
import { getApiUrl } from '../../utils/apiUrl';

// Get API URL for this environment
const API_URL = getApiUrl();

// Default images URLs from the original WordPress site
const DEFAULT_IMAGES = {
  logo: 'https://2025.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-360-EVENT-LOGO-sans-fond-vignette-1-1024x1024.png',
  mainImage: 'https://2025.justasmile-events.com/wp-content/uploads/2025/09/image00002-1-683x1024.jpeg',
  // Gallery images showing the VideoBooth from different angles (matching reference site)
  gallery: [
    'https://2025.justasmile-events.com/wp-content/uploads/2023/04/AHD_3442-1024x684.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2023/04/AHD_3444-scaled-e1683203859904-1024x683.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2023/04/AHD_3471-1024x684.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2023/04/AHD_3489-1024x684.jpg',
  ],
  models: [
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/30-ans-Tiffany.png', alt: '30 ans Tiffany' },
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/bapteme-2.png', alt: 'bapteme 2' },
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/bapteme.png', alt: 'bapteme' },
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/dragonball.png', alt: 'dragonball' },
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/mariage.png', alt: 'mariage' },
  ],
};

// Sharing methods with icons
const SHARING_METHODS = [
  { id: 'airdrop', label: 'AirDrop', icon: FiWifi, color: '#007AFF' },
  { id: 'qrcode', label: 'QR Code', icon: BsQrCode, color: '#e6d28e' },
  { id: 'sms', label: 'SMS', icon: FiMessageSquare, color: '#34C759' },
  { id: 'whatsapp', label: 'WhatsApp', icon: BsWhatsapp, color: '#25D366' },
  { id: 'email', label: 'Email', icon: FiMail, color: '#FF9500' },
];

// Default Hotspot data for VideoBooth 360 (matching https://2025.justasmile-events.com/location-de-videobooth/)
const DEFAULT_HOTSPOTS = [
  {
    id: 'ringlight',
    label: 'La ringlight',
    icon: 'sun',
    tooltip: 'Une ringlight professionnelle',
    positionLeft: '25%',
    positionTop: '22%',
  },
  {
    id: 'camera',
    label: 'La caméra',
    icon: 'video',
    tooltip: 'Une caméra 4K de haute qualité',
    positionLeft: '40%',
    positionTop: '35%',
  },
  {
    id: 'platform',
    label: 'La plateforme',
    icon: 'rotate',
    tooltip: 'Une plateforme de 80 cm ou de 1 mètre de diamètre pouvant accueillir entre 3 et 5 personnes selon la taille choisie',
    positionLeft: '50%',
    positionTop: '82%',
  },
  {
    id: 'led',
    label: 'Lumières LED',
    icon: 'music',
    tooltip: 'Multi color & sans fils',
    positionLeft: '75%',
    positionTop: '48%',
  },
];

// Default FAQ data
const DEFAULT_FAQ = [
  {
    question: 'Pourquoi louer un VideoBooth 360 chez Just a Smile Events ?',
    answer: `<p>Pour rendre votre événement <strong>ORIGINAL</strong> !</p>
<p>Nos graphistes créent pour chaque événement un filtre personnalisé en fonction de votre thème et de vos envies.</p>
<p>Nous mettons aussi tout en œuvre pour rendre l'activité <strong>MAGIQUE</strong> et en mettre plein la vue à vos invités en installant notre VideoBooth avec un tapis rouge, des poteaux or de guidage, des cordes de couleur en fonction de votre décoration, des lumières d'appoint, une station de partage, des accessoires ludiques… et surtout <strong>des ACCESSOIRES VIP</strong>.</p>
<p>Notre PhotoBooth 360° peut aussi bien être utilisé de jour comme de nuit, en intérieur comme en extérieur.</p>
<p>Étant une prestation <strong>HAUT DE GAMME</strong>, nous mettons à votre disposition deux hôtes ou hôtesses pour gérer l'activité et la rendre <strong>GRANDIOSE</strong> !</p>
<p>Nous proposons un service professionnel et de qualité.</p>`,
  },
  {
    question: 'Quel matériel utilisons-nous ?',
    answer: `<p>Nous proposons au choix 2 VideoBooth :</p>
<ul>
<li>Un VideoBooth composé d'une plateforme de 80 cm de diamètre pouvant accueillir jusqu'à 3 personnes simultanément</li>
<li>Un VideoBooth composé d'une plateforme de 1 mètre de diamètre pouvant accueillir jusqu'à 5 personnes simultanément</li>
</ul>
<p>Nos VideoBooth sont composés également de :</p>
<ul>
<li>Une caméra 4K haute qualité</li>
<li>Une ringlight professionnelle</li>
</ul>
<p>Nous utilisons aussi pour perfectionner nos prestations :</p>
<ul>
<li>Un iPad Pro faisant office de station de partage</li>
<li>Une télévision dernière génération permettant de visionner en diaporama et en temps réel toutes les vidéos prises par le VideoBooth lors de l'événement</li>
<li>Divers accessoires pour rendre l'animation encore plus amusante</li>
</ul>`,
  },
  {
    question: 'Comment fonctionne le VideoBooth 360 ?',
    answer: `<p>Rien de plus facile !</p>
<p>Vous montez sur la plateforme sur laquelle vos plus beaux pas de danse, sourires et délires sont attendus. En quelques secondes la caméra 4K fixée sur le bras rotatif vous filme en 360°.</p>
<p>Notre logiciel réalise pour vous en un instant un montage digne des plus grandes productions en y ajoutant votre filtre personnalisé, de la musique, des effets spéciaux, du ralenti…</p>`,
  },
  {
    question: 'Comment se passe l\'installation ?',
    answer: `<p>Pas de panique ! L'installation est rapide, notre équipe a besoin d'environ 1 Heure pour que le dispositif soit mis en place et fonctionnel.</p>
<p>Pour une installation en extérieur, le VideoBooth doit obligatoirement être installé à l'ombre ou sous un abri. Si la météo ne le permet pas, il sera automatiquement installé en intérieur.</p>
<p>Nous avons besoin d'une surface plane de <strong>10m²</strong>.</p>`,
  },
  {
    question: 'Quels sont les différents formats vidéo ?',
    answer: `<p>Notre caméra professionnelle a la possibilité de vous filmer sous différents angles :</p>
<ul>
<li><strong>Vertical</strong> (Format story)</li>
<li><strong>Horizontal</strong> (Format vidéo YouTube)</li>
<li><strong>Carré</strong> (Format publication Instagram)</li>
</ul>`,
  },
];

// Icon mapping - supports both text keys and emoji keys
const ICON_MAP = {
  video: FiVideo,
  rotate: FiRotateCw,
  sun: FiSun,
  music: FiMusic,
  // Emoji mappings for database data
  '🎥': FiVideo,
  '📹': FiVideo,
  '💡': FiSun,
  '☀️': FiSun,
  '🎵': FiMusic,
  '🔄': FiRotateCw,
  '⭐': FiStar,
};

export default function VideoBooth360PresentationPage({ pageData, productData, onNavigate, onBookingClick }) {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [currentModelIndex, setCurrentModelIndex] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [hotspotsFromDB, setHotspotsFromDB] = useState(null);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleReviewsInfo, setGoogleReviewsInfo] = useState({ rating: 5.0, total: 89 });

  // Load hotspots from database
  useEffect(() => {
    const loadHotspots = async () => {
      try {
        const response = await fetch(`${API_URL}/api/hotspots-config`);
        if (response.ok) {
          const data = await response.json();
          if (data['videobooth-360']) {
            setHotspotsFromDB(data['videobooth-360']);
          }
        }
      } catch (error) {
        console.log('Using default hotspots');
      }
    };
    loadHotspots();
  }, []);

  // Load Google Reviews (same as homepage)
  useEffect(() => {
    const loadGoogleReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/google-reviews/`);
        if (response.ok) {
          const data = await response.json();
          if (data.reviews && data.reviews.length > 0) {
            const formattedReviews = data.reviews.map(r => ({
              name: r.author_name || r.name,
              rating: r.rating,
              text: r.text,
              date: r.relative_time_description || r.date,
              avatar: r.profile_photo_url || r.avatar
            }));
            setGoogleReviews(formattedReviews);
            setGoogleReviewsInfo({
              rating: data.average_rating || 5.0,
              total: data.total_reviews || formattedReviews.length
            });
          }
        }
      } catch (error) {
        console.log('Using fallback reviews');
      }
    };
    loadGoogleReviews();
  }, []);

  // Helper to find section by type
  const getSection = (type) => {
    return pageData?.sections?.find(s => s.type === type)?.content || null;
  };

  // Get content from pageData sections - check for actual content
  const heroSection = getSection('hero_video') || getSection('hero') || {};
  const textImageSection = getSection('text_image') || {};
  const hotspotsSection = getSection('hotspots') || {};
  const faqSection = getSection('faq') || {};
  const reviewsSection = getSection('reviews') || {};
  const modelsSection = getSection('models') || getSection('models_gallery') || {};
  const gallerySection = getSection('presentation_gallery') || getSection('gallery') || {};

  // Map sections to expected content structure
  const heroContent = {
    title: heroSection.title || 'LOCATION DE VIDEOBOOTH 360',
    subtitle: heroSection.subtitle || '',
    videoUrl: heroSection.videoUrl || 'https://www.youtube.com/watch?v=QfrafL_Bcg4',
    buttonText80: heroSection.buttonText80 || 'RÉSERVATION 80CM',
    buttonText100: heroSection.buttonText100 || 'RÉSERVATION 100CM',
    backgroundImage: heroSection.backgroundImage || heroSection.image || '',
    overlayOpacity: heroSection.overlayOpacity || 0.6,
  };
  
  const presentationContent = {
    title: textImageSection.title || '',
    text: textImageSection.text || '',
    highlightText: textImageSection.highlightText || '',
    image: textImageSection.image || '',
    paragraphs: textImageSection.paragraphs || (textImageSection.text ? [textImageSection.text] : []),
  };
  
  const hotspotsContent = hotspotsSection || {};
  const modelsContent = modelsSection || {};
  const faqContent = faqSection || {};
  const reviewsContent = reviewsSection || {};

  // Installation video URL (separate from hero background video)
  const installationVideoSection = getSection('installation_video') || {};
  const installationVideoUrl = installationVideoSection.videoUrl || heroSection.installationVideoUrl || 'https://www.youtube.com/watch?v=QfrafL_Bcg4';

  // Images
  const IMAGES = {
    logo: presentationContent.logo || DEFAULT_IMAGES.logo,
    mainImage: hotspotsFromDB?.image || hotspotsContent.mainImage || DEFAULT_IMAGES.mainImage,
    gallery: gallerySection?.images || presentationContent.galleryImages || DEFAULT_IMAGES.gallery,
    models: modelsContent.carouselImages || DEFAULT_IMAGES.models,
  };

  // Extract YouTube video ID - use Constructor only (Option B)
  const getYouTubeId = (url) => {
    // Get from Constructor URL
    if (url) {
      const match = url.match(/(?:v=|\/embed\/|youtu\.be\/)([^&\?\/]+)/);
      if (match) {
        return match[1];
      }
    }
    // Default fallback (vidéo par défaut si rien configuré dans le Constructor)
    return 'QfrafL_Bcg4';
  };
  const videoId = getYouTubeId(heroContent.videoUrl);

  // Hotspots - Use DB data (x/y format) or fall back to section items or defaults
  const HOTSPOTS = (hotspotsFromDB?.hotspots || hotspotsContent.items || DEFAULT_HOTSPOTS).map(h => ({
    ...h,
    id: h.id,
    label: h.label,
    tooltip: h.tooltip || h.description || '',
    icon: ICON_MAP[h.icon] || FiVideo,
    position: { 
      left: h.positionLeft || `${h.x}%`, 
      top: h.positionTop || `${h.y}%` 
    }
  }));

  // FAQ
  const FAQ_DATA = faqContent.items || DEFAULT_FAQ;

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentModelIndex((prev) => (prev + 1) % IMAGES.models.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, IMAGES.models.length]);

  const nextModel = () => {
    setIsAutoPlaying(false);
    setCurrentModelIndex((prev) => (prev + 1) % IMAGES.models.length);
  };

  const prevModel = () => {
    setIsAutoPlaying(false);
    setCurrentModelIndex((prev) => (prev - 1 + IMAGES.models.length) % IMAGES.models.length);
  };

  // Text content with defaults - check for actual content, not just empty arrays
  const presentationTitle = presentationContent.title || "<strong>Just a Smile Events</strong> vous propose la location de <strong>Videobooth 360</strong> pour immortaliser vos événements.";
  
  // PRIORITIZE text field from Constructor over paragraphs
  const presentationText = presentationContent.text || "";
  const presentationParagraphs = presentationText 
    ? [presentationText]
    : (presentationContent.paragraphs && presentationContent.paragraphs.length > 0) 
      ? presentationContent.paragraphs 
      : [
          "Vous souhaitez profiter de la nouvelle activité <strong>TENDANCE</strong> et épater vos invités ? C'est ici que tout se passe !",
          "Oublions le principe d'un PhotoBooth classique.",
          "Le VideoBooth vous permet de créer vos propres vidéos 4K en vous filmant à 360°. Une façon très amusante, originale et unique pour immortaliser votre événement.",
          "Peu importe l'âge de vos convives, le VideoBooth fera sensation !"
        ];
  
  const hotspotsTitle = hotspotsContent.title || "Qu'est-ce qu'un <strong>VideoBooth 360 ?</strong>";
  
  // Use description fields from Constructor, fallback to paragraphs array, then defaults
  const hotspotsParagraphs = (hotspotsContent.description || hotspotsContent.description2)
    ? [hotspotsContent.description, hotspotsContent.description2].filter(Boolean)
    : (hotspotsContent.paragraphs && hotspotsContent.paragraphs.length > 0)
      ? hotspotsContent.paragraphs
      : [
          "<strong>Il s'agit d'un PhotoBooth en 360° qui permet d'enregistrer des vidéos souvenirs pour tous types d'événements : anniversaire, mariage, baptême, soirées d'entreprise, clips vidéos, EVJF/EVJG, baby shower, gender reveal…</strong>",
          "<strong>Chaque invité repart avec une ou plusieurs vidéos après l'avoir récupéré sur notre sharing station via différents supports (AirDrop, QRCode, SMS, WhatsApp, Mail…)</strong>"
        ];
  
  const modelsTitle = modelsContent.title || "<strong>Des modèles personnalisés</strong> pour un résultat unique !";
  const modelsText = modelsContent.text || "Nos graphistes créent pour chaque événement un ou plusieurs modèles en fonction de votre thème et de vos envies.";
  const modelsButtonText = modelsContent.buttonText || "Découvrir nos réalisations";
  const modelsButtonLink = modelsContent.buttonLink || "videobooth-360-realisations";
  
  const faqTitle = faqContent.title || "Vous avez des questions ? Consultez notre FAQ :";
  const reviewsTitle = reviewsContent.title || "Qui de mieux que vous, pour en parler ?";

  return (
    <div className="videobooth-360-presentation-page bg-white" data-testid="videobooth-360-presentation-page">
      {/* Section 1: Hero with YouTube Video Background - Reduced Height */}
      <section className="relative h-[85vh] flex items-end justify-center overflow-hidden pb-24">
        {/* YouTube Video Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-150"
            style={{ 
              pointerEvents: 'none',
              border: 'none',
              width: '100vw',
              height: '100vh',
              minWidth: '177.78vh',
              minHeight: '100vh'
            }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="VideoBooth 360 Video"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Wave divider at bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" preserveAspectRatio="none" className="w-full h-16">
            <path fill="#000000" d="M421.9,6.5c22.6-2.5,51.5,0.4,75.5,5.3c23.6,4.9,70.9,23.5,100.5,35.7c75.8,32.2,133.7,44.5,192.6,49.7c23.6,2.1,48.7,3.5,103.4-2.5c54.7-6,106.2-25.6,106.2-25.6V100H0V30.3c0,0,72,32.6,158.4,30.5c39.2-0.7,92.8-6.7,134-22.4c21.2-8.1,52.2-18.2,79.7-24.2C399.3,7.9,411.6,7.5,421.9,6.5z"></path>
          </svg>
        </div>

        {/* Content - Positioned near the wave */}
        <div className="relative z-10 text-center px-6 mb-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-wide"
          >
            {heroContent.title}
          </motion.h2>
          {heroContent.subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto"
            >
              {heroContent.subtitle}
            </motion.p>
          )}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            onClick={(e) => onBookingClick?.(e, 'videobooth-360')}
            className="bg-[#e6d28e] hover:bg-[#d4c67a] text-black font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            data-testid="hero-reservation-btn"
          >
            {heroContent.buttonText || 'RÉSERVATION'}
          </motion.button>
        </div>
      </section>

      {/* Section 2: Presentation with Logo + Text + Gallery (matching reference site) */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left: Logo + Text */}
            <div className="relative text-white">
              <div className="text-center md:text-left">
                <img
                  src={IMAGES.logo}
                  alt="Just A 360 Event Logo"
                  className="w-40 h-40 mx-auto md:mx-0 mb-6 object-contain"
                />
                <h2 
                  className="text-2xl md:text-3xl font-medium text-white mb-6 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: presentationTitle }}
                />
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  {presentationParagraphs.map((p, idx) => (
                    <p key={idx} dangerouslySetInnerHTML={{ __html: p }} />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Presentation Video (replaces gallery) */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="transform rotate-2 rounded-xl overflow-hidden shadow-2xl"
              >
                {/* Video Embed */}
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${(() => {
                      const videoUrl = installationVideoUrl || heroContent.videoUrl || '';
                      const match = videoUrl.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&?\n]+)/);
                      return match ? match[1] : 'QfrafL_Bcg4';
                    })()}?rel=0&modestbranding=1`}
                    title="Présentation VideoBooth 360"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2.5: Interactive Hotspots Image */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Hotspot Image */}
            <div className="relative w-full max-w-md mx-auto">
              <img
                src={IMAGES.mainImage}
                alt="VideoBooth 360"
                className="w-full rounded-2xl shadow-2xl"
              />
              
              {/* Hotspots - Visible on all devices - Icon only, expands on click */}
              <div className="absolute inset-0">
                {HOTSPOTS.map((hotspot) => {
                  const IconComponent = hotspot.icon;
                  const isActive = activeHotspot === hotspot.id;
                  return (
                    <div
                      key={hotspot.id}
                      className="absolute"
                      style={{ left: hotspot.position.left, top: hotspot.position.top }}
                    >
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        animate={{ 
                          scale: isActive ? 1 : [1, 1.08, 1],
                          boxShadow: isActive 
                            ? '0 0 20px rgba(230, 210, 142, 0.8)' 
                            : ['0 0 0px rgba(230, 210, 142, 0)', '0 0 15px rgba(230, 210, 142, 0.6)', '0 0 0px rgba(230, 210, 142, 0)']
                        }}
                        transition={{ 
                          duration: 1.5, 
                          repeat: isActive ? 0 : Infinity,
                          ease: "easeInOut"
                        }}
                        onClick={() => setActiveHotspot(isActive ? null : hotspot.id)}
                        className={`flex items-center justify-center rounded-full shadow-lg transition-all duration-300 ${
                          isActive
                            ? 'bg-[#e6d28e] text-black z-20 px-3 py-2 gap-2'
                            : 'bg-white/90 text-gray-800 hover:bg-[#e6d28e] hover:text-black w-8 h-8 sm:w-10 sm:h-10'
                        }`}
                      >
                        <IconComponent className={`flex-shrink-0 ${isActive ? 'w-4 h-4' : 'w-4 h-4 sm:w-5 sm:h-5'}`} />
                        {/* Show label only when active */}
                        <AnimatePresence>
                          {isActive && (
                            <motion.span
                              initial={{ opacity: 0, width: 0 }}
                              animate={{ opacity: 1, width: 'auto' }}
                              exit={{ opacity: 0, width: 0 }}
                              className="text-xs sm:text-sm font-medium whitespace-nowrap overflow-hidden"
                            >
                              {hotspot.label}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </motion.button>
                      
                      {/* Tooltip with description */}
                      <AnimatePresence>
                        {isActive && hotspot.tooltip && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 p-3 bg-gray-900 rounded-lg shadow-xl border border-gray-700 z-20"
                          >
                            <p className="text-sm text-gray-300">{hotspot.tooltip}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
              
              {/* Mobile: Feature list below image - interactive */}
            </div>

            {/* Right: Qu'est-ce qu'un VideoBooth 360 ? */}
            <div>
              <h2 
                className="text-3xl md:text-4xl font-bold mb-8"
              >
                <span className="text-white">Découvrez notre </span>
                <span className="gold-shimmer-text">VideoBooth 360</span>
              </h2>
              
              {/* Split content: before and after "Résumé essentiel" to insert sharing icons */}
              {(() => {
                // Find the index where "Résumé essentiel" appears
                const resumeIndex = hotspotsParagraphs.findIndex(p => 
                  p.toLowerCase().includes('résumé essentiel') || p.toLowerCase().includes('resume essentiel')
                );
                
                const beforeResume = resumeIndex > 0 ? hotspotsParagraphs.slice(0, resumeIndex) : hotspotsParagraphs;
                const afterResume = resumeIndex > 0 ? hotspotsParagraphs.slice(resumeIndex) : [];
                
                return (
                  <>
                    {/* Content before Résumé essentiel */}
                    <div className="videobooth-description-text text-lg text-white space-y-6 [&_p]:text-white [&_strong]:font-bold [&_strong]:text-[#e6d28e] [&_em]:italic [&_u]:underline [&_li]:text-white [&_span]:text-white" style={{ color: 'white' }}>
                      {beforeResume.map((p, idx) => (
                        <div key={idx} className="text-white" style={{ color: 'white' }} dangerouslySetInnerHTML={{ __html: p.replace(/color:\s*[^;]+;?/gi, 'color: white;') }} />
                      ))}
                    </div>
                    
                    {/* Sharing Methods with Icons - Now placed BEFORE Résumé essentiel */}
                    <div className="my-8">
                      <p className="text-white mb-4 font-medium">Modes de partage disponibles :</p>
                      <div className="flex flex-wrap gap-3 md:gap-4">
                        {SHARING_METHODS.map((method) => {
                          const IconComponent = method.icon;
                          return (
                            <motion.div
                              key={method.id}
                              initial={{ opacity: 0, y: 10 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              whileHover={{ scale: 1.05 }}
                              className="flex items-center gap-2 px-4 py-2 bg-gray-800 rounded-full border border-gray-700"
                            >
                              <IconComponent className="w-5 h-5" style={{ color: method.color }} />
                              <span className="text-white text-sm font-medium">{method.label}</span>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Content after (including Résumé essentiel) */}
                    {afterResume.length > 0 && (
                      <div className="videobooth-description-text text-lg text-white space-y-6 [&_p]:text-white [&_strong]:font-bold [&_strong]:text-[#e6d28e] [&_em]:italic [&_u]:underline [&_li]:text-white [&_span]:text-white" style={{ color: 'white' }}>
                        {afterResume.map((p, idx) => (
                          <div key={`after-${idx}`} className="text-white" style={{ color: 'white' }} dangerouslySetInnerHTML={{ __html: p.replace(/color:\s*[^;]+;?/gi, 'color: white;') }} />
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: FAQ */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            <span className="gold-shimmer-text">{faqTitle}</span>
          </h2>
          
          <div className="space-y-4">
            {FAQ_DATA.map((item, index) => (
              <div
                key={index}
                className="border border-gray-700 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left bg-gray-900 hover:bg-gray-800 transition-colors"
                >
                  <span className="font-semibold text-white pr-4">{item.question}</span>
                  <FiChevronDown
                    className={`h-5 w-5 text-[#e6d28e] transition-transform flex-shrink-0 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaqIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div 
                        className="p-5 pt-0 text-gray-300 prose prose-sm prose-invert max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-4 [&_li]:my-1 [&_strong]:text-white [&_strong]:font-bold"
                        dangerouslySetInnerHTML={{ 
                          __html: (item.answer || '')
                            .replace(/&lt;/g, '<')
                            .replace(/&gt;/g, '>')
                            .replace(/&amp;/g, '&')
                            .replace(/&quot;/g, '"')
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews section removed - using scrolling marquee in Footer instead */}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {showLightbox && lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-5xl max-h-[90vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowLightbox(false)}
                className="absolute -top-12 right-0 text-white/80 hover:text-white text-4xl font-light z-10"
              >
                ×
              </button>
              <img
                src={lightboxImage}
                alt="Gallery"
                className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
